<template>
  <div>
    <v-card class="rounded-lg elevation-3 overflow-hidden">
      <!-- Toolbar Header Principal -->
      <v-toolbar
        color="secondary"
        dark
        flat
        class="table-header-toolbar px-2 px-sm-3"
      >
        <div
          class="d-flex align-center header-main-info flex-grow-1 min-width-0"
        >
          <div class="header-icon-box mr-2 mr-sm-3 flex-shrink-0">
            <v-icon color="white">mdi-file-document-multiple-outline</v-icon>
          </div>
          <div class="header-text-block min-width-0 mr-2 mr-sm-3">
            <div class="header-company-title font-weight-bold text-truncate">
              {{ Titulo || "Administración de Reportes" }}
            </div>
          </div>
          <v-chip
            small
            color="white"
            text-color="secondary"
            class="header-count-chip font-weight-bold elevation-1 flex-shrink-0"
          >
            <v-icon left size="16" color="secondary">mdi-counter</v-icon>
            {{ Items ? Items.length : 0 }} Reportes
          </v-chip>
        </div>

        <div class="header-help-btn ml-1 ml-sm-2 flex-shrink-0">
          <!-- Botón de Ayuda -->
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="mx-1 btn-circle-tooltip"
                icon
                dark
                target="_blank"
                :href="global.helpdesk_url + 'plantas'"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="25" color="amber lighten-2"
                  >mdi-help-circle-outline</v-icon
                >
              </v-btn>
            </template>
            <span>¿Necesitas ayuda?</span>
          </v-tooltip>
        </div>
      </v-toolbar>

      <!-- Sección de Búsqueda y Acciones -->
      <div class="pa-2 pa-sm-4 pb-2">
        <div
          class="d-flex align-center justify-space-between flex-wrap gap-2 mb-3"
        >
          <!-- Búsqueda Rápida -->
          <div class="search-field flex-grow-1 mr-md-3">
            <v-text-field
              v-model="search"
              outlined
              dense
              single-line
              hide-details
              prepend-inner-icon="mdi-magnify"
              clearable
              label="Búsqueda rápida"
              placeholder="Buscar por usuario, tipo de reporte, estado..."
              class="rounded-lg"
            ></v-text-field>
          </div>

          <!-- Botones de Acción -->
          <div
            class="action-buttons-group d-flex align-center flex-wrap gap-2 mt-2 mt-md-0"
          >
            <!-- Descargar Excel -->
            <v-btn
              v-if="Excel"
              color="success"
              dark
              height="38"
              class="btn-action-header px-4 font-weight-bold elevation-1 text-none rounded-lg mr-sm-2"
              @click="DescargarExcel()"
            >
              <v-icon left size="18">mdi-file-excel</v-icon>
              Descargar Tabla
            </v-btn>

            <!-- Botón Nuevo (si aplica) -->
            <v-btn
              v-if="Crear"
              color="primary"
              dark
              height="38"
              class="btn-action-header px-4 font-weight-bold elevation-1 text-none rounded-lg"
              @click="nuevoItem()"
            >
              <v-icon left size="20">mdi-plus-circle</v-icon>
              Nuevo
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Indicador de desplazamiento horizontal en celular -->
      <div
        class="d-flex d-sm-none align-center text-caption primary--text mb-2 px-3 pt-1"
      >
        <v-icon x-small color="primary" class="mr-1">
          mdi-gesture-swipe-horizontal
        </v-icon>
        <span>Desliza horizontalmente para ver todos los datos</span>
      </div>

      <!-- Tabla Principal -->
      <div class="table-responsive-wrapper">
        <v-data-table
          dense
          :headers="Cabecera"
          :items="Items"
          :items-per-page="10"
          :search="search"
          :mobile-breakpoint="0"
          :loading="Loading"
          loading-text="Cargando reportes... Por favor, espere"
          class="modern-table elevation-0"
          no-data-text="No hay reportes disponibles"
          :footer-props="{
            'items-per-page-text': 'Resultados por página:',
            'items-per-page-options': [5, 10, 25, 50],
            'page-text': '{0}-{1} de {2}',
          }"
        >
          <!-- Columna Nombre Completo / Usuario -->
          <template v-slot:[`item.name`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="28" color="grey lighten-4" class="mr-2">
                <v-icon size="16" color="secondary">mdi-account</v-icon>
              </v-avatar>
              <span class="font-weight-medium text-body-2 text--primary">
                {{ item.name || "Sin usuario" }}
              </span>
            </div>
          </template>

          <!-- Columna Reporte -->
          <template v-slot:[`item.reporte`]="{ item }">
            <span class="font-weight-bold text-body-2 text--primary">
              {{ item.reporte }}
            </span>
          </template>

          <!-- Columna Fecha (hora:min:seg dia-mes-año) -->
          <template v-slot:[`item.fecha`]="{ item }">
            <div
              class="d-flex align-center justify-center text-caption text--secondary font-mono"
            >
              <v-icon x-small color="grey darken-1" class="mr-1"
                >mdi-calendar-clock</v-icon
              >
              <span>{{ formatearFechaHora(item.fecha) }}</span>
            </div>
          </template>

          <!-- Columna Reporte Generado / Acciones de Reporte (PDF, Comentar) -->
          <template v-slot:[`item.iconos`]="{ item }">
            <div class="d-flex align-center justify-center gap-1">
              <!-- Botón Ver PDF -->
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    height="28"
                    width="28"
                    color="red darken-1"
                    fab
                    x-small
                    dark
                    class="action-btn elevation-1"
                    @click="ver(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon x-small>mdi-file-pdf-box</v-icon>
                  </v-btn>
                </template>
                <span>Ver documento PDF</span>
              </v-tooltip>

              <!-- Botón Comentarios -->
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    height="28"
                    width="28"
                    color="amber darken-3"
                    fab
                    x-small
                    dark
                    class="action-btn elevation-1"
                    @click="comentar(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon x-small>mdi-comment-text-outline</v-icon>
                  </v-btn>
                </template>
                <span>Observaciones y Comentarios</span>
              </v-tooltip>
            </div>
          </template>

          <!-- Columna Estado -->
          <template v-slot:[`item.estado`]="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                small
                v-if="item.estado === 'APROBADO'"
                color="green lighten-5"
                text-color="green darken-3"
                class="font-weight-bold elevation-1"
              >
                <v-icon left x-small color="green darken-2"
                  >mdi-check-circle</v-icon
                >
                Aprobado
              </v-chip>
              <v-chip
                small
                v-else-if="item.estado === 'RECHAZADO'"
                color="red lighten-5"
                text-color="red darken-3"
                class="font-weight-bold elevation-1"
              >
                <v-icon left x-small color="red darken-2"
                  >mdi-close-circle</v-icon
                >
                Rechazado
              </v-chip>
              <v-chip
                small
                v-else
                color="amber lighten-5"
                text-color="amber darken-4"
                class="font-weight-bold elevation-1"
              >
                <v-icon left x-small color="amber darken-3"
                  >mdi-clock-outline</v-icon
                >
                {{ item.estado || "Pendiente" }}
              </v-chip>
            </div>
          </template>

          <!-- Columna Aprobación / Acciones de Flujo -->
          <template v-slot:[`item.acciones`]="{ item }">
            <div class="d-flex align-center justify-center gap-1">
              <!-- Aprobar -->
              <v-tooltip bottom v-if="item.estado !== 'APROBADO'">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    height="28"
                    width="28"
                    color="green darken-1"
                    fab
                    x-small
                    dark
                    class="action-btn elevation-1"
                    @click="aprobarItem(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon x-small>mdi-check-bold</v-icon>
                  </v-btn>
                </template>
                <span>Aprobar reporte</span>
              </v-tooltip>

              <!-- Rechazar -->
              <v-tooltip bottom v-if="item.estado !== 'RECHAZADO'">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    height="28"
                    width="28"
                    color="red darken-1"
                    fab
                    x-small
                    dark
                    class="action-btn elevation-1"
                    @click="rechazarItem(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon x-small>mdi-close-thick</v-icon>
                  </v-btn>
                </template>
                <span>Rechazar reporte</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <!-- Modal Comentarios -->
    <Comentario
      :dialog="dialog"
      @dialog="dialog = $event"
      :Item="itemSelected"
    />

    <!-- Modal Visor PDF -->
    <v-dialog
      v-model="dialogPDF"
      persistent
      :max-width="ver_iframe ? '88%' : '480px'"
      transition="dialog-transition"
    >
      <v-card class="rounded-lg elevation-3 overflow-hidden">
        <v-toolbar
          color="secondary"
          dark
          flat
          class="table-header-toolbar px-2 px-sm-4"
        >
          <div
            class="d-flex align-center header-main-info flex-grow-1 min-width-0"
          >
            <div class="header-icon-box mr-2 mr-sm-3 flex-shrink-0">
              <v-icon color="white">mdi-file-pdf-box</v-icon>
            </div>
            <div class="header-text-block min-width-0 mr-2 mr-sm-3">
              <div class="header-company-title font-weight-bold text-truncate">
                {{
                  ver_iframe ? "Reporte Consolidado (PDF)" : "Generando Reporte"
                }}
              </div>
            </div>
          </div>
          <div class="header-help-btn ml-1 ml-sm-2 flex-shrink-0">
            <v-btn icon dark @click="cerrarVisor" class="btn-circle-tooltip">
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-toolbar>

        <v-card-text class="pa-3 pa-sm-4 pa-md-5">
          <!-- Loader con mensaje de estado -->
          <div
            v-if="loader"
            class="d-flex flex-column align-center justify-center py-10"
          >
            <v-progress-circular
              indeterminate
              color="secondary"
              :size="56"
              :width="5"
              class="mb-4"
            ></v-progress-circular>
            <div class="text-h6 font-weight-medium mb-1 text--primary">
              Procesando documento...
            </div>
            <div class="body-2 text--secondary text-center">
              {{ loaderMensaje }}
            </div>
          </div>

          <!-- iframe del PDF -->
          <div
            v-if="ver_iframe && pdfUrl"
            class="rounded-lg overflow-hidden border pdf-iframe-container"
            style="border: 1px solid #e2e8f0"
          >
            <iframe id="ifrm" :src="pdfUrl" class="pdf-iframe-view"></iframe>
          </div>
        </v-card-text>

        <v-card-actions
          class="form-card-actions pa-3 pa-sm-4 bg-light border-top d-flex justify-end"
        >
          <v-btn
            outlined
            color="grey darken-1"
            class="btn-form-action text-capitalize px-4 rounded-lg font-weight-medium"
            @click="cerrarVisor"
          >
            <v-icon left small>mdi-close</v-icon>
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Rechazar Reporte -->
    <v-dialog v-model="dialogRechazar" max-width="520px" persistent>
      <v-card class="rounded-lg elevation-3 overflow-hidden">
        <v-toolbar
          color="secondary"
          dark
          flat
          class="table-header-toolbar px-2 px-sm-4"
        >
          <div
            class="d-flex align-center header-main-info flex-grow-1 min-width-0"
          >
            <div class="header-icon-box mr-2 mr-sm-3 flex-shrink-0">
              <v-icon color="red lighten-3">mdi-alert-circle-outline</v-icon>
            </div>
            <div class="header-text-block min-width-0 mr-2 mr-sm-3">
              <div class="header-company-title font-weight-bold text-truncate">
                Rechazar Reporte
              </div>
            </div>
          </div>
          <div class="header-help-btn ml-1 ml-sm-2 flex-shrink-0">
            <v-btn
              icon
              dark
              @click="cerrarModalRechazar"
              class="btn-circle-tooltip"
              :disabled="loadingRechazo"
            >
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-toolbar>

        <v-card-text class="pa-3 pa-sm-4 pa-md-5">
          <div class="section-title d-flex align-center mb-3">
            <v-icon color="secondary" small class="mr-2"
              >mdi-comment-alert-outline</v-icon
            >
            <span class="text-subtitle-2 font-weight-bold text--primary">
              Motivo del Rechazo
            </span>
          </div>

          <v-alert
            dense
            text
            type="error"
            icon="mdi-alert-circle"
            class="rounded-lg mb-4 text-caption"
          >
            Por favor, detalle la razón por la cual este reporte está siendo
            rechazado. Este comentario quedará registrado en el historial.
          </v-alert>

          <v-form
            ref="formRechazo"
            v-model="validRechazo"
            @submit.prevent="confirmarRechazo"
          >
            <v-textarea
              v-model="comentarioRechazo"
              label="Comentario de Rechazo *"
              placeholder="Describa los motivos u observaciones para rechazar el reporte..."
              rows="3"
              outlined
              dense
              auto-grow
              prepend-inner-icon="mdi-comment-remove-outline"
              class="rounded-lg"
              hide-details="auto"
              :rules="[
                (v) =>
                  !!(v && v.trim()) ||
                  'El comentario es obligatorio para rechazar el reporte',
              ]"
              required
            ></v-textarea>
          </v-form>

          <div class="d-flex align-center mt-3 text-caption text--secondary">
            <v-icon x-small color="error" class="mr-1">mdi-asterisk</v-icon>
            <span>Campo obligatorio</span>
          </div>
        </v-card-text>

        <v-card-actions
          class="form-card-actions pa-3 pa-sm-4 bg-light border-top d-flex justify-end align-center"
        >
          <v-btn
            outlined
            color="grey darken-1"
            class="btn-form-action text-capitalize px-4 rounded-lg mr-sm-2 font-weight-medium"
            @click="cerrarModalRechazar"
            :disabled="loadingRechazo"
          >
            <v-icon left small>mdi-close</v-icon>
            Cancelar
          </v-btn>

          <v-btn
            :color="puedeRechazar ? 'red darken-1' : 'grey lighten-2'"
            :dark="puedeRechazar"
            elevation="2"
            class="btn-form-action btn-rechazar text-capitalize px-5 rounded-lg font-weight-bold"
            @click="confirmarRechazo"
            :loading="loadingRechazo"
            :disabled="!puedeRechazar || loadingRechazo"
          >
            <v-icon left small>mdi-close-thick</v-icon>
            Rechazar Reporte
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Aprobar Reporte -->
    <v-dialog v-model="dialogAprobar" max-width="520px" persistent>
      <v-card class="rounded-lg elevation-3 overflow-hidden">
        <v-toolbar
          color="secondary"
          dark
          flat
          class="table-header-toolbar px-2 px-sm-4"
        >
          <div
            class="d-flex align-center header-main-info flex-grow-1 min-width-0"
          >
            <div class="header-icon-box mr-2 mr-sm-3 flex-shrink-0">
              <v-icon color="green lighten-3">mdi-check-circle-outline</v-icon>
            </div>
            <div class="header-text-block min-width-0 mr-2 mr-sm-3">
              <div class="header-company-title font-weight-bold text-truncate">
                Aprobar Reporte
              </div>
            </div>
          </div>
          <div class="header-help-btn ml-1 ml-sm-2 flex-shrink-0">
            <v-btn
              icon
              dark
              @click="cerrarModalAprobar"
              class="btn-circle-tooltip"
              :disabled="loadingAprobar"
            >
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-toolbar>

        <v-card-text class="pa-3 pa-sm-4 pa-md-5">
          <div class="section-title d-flex align-center mb-3">
            <v-icon color="secondary" small class="mr-2"
              >mdi-check-decagram-outline</v-icon
            >
            <span class="text-subtitle-2 font-weight-bold text--primary">
              Confirmación de Aprobación
            </span>
          </div>

          <v-alert
            dense
            text
            type="success"
            icon="mdi-check-circle-outline"
            class="rounded-lg mb-4 text-caption"
          >
            ¿Confirma que desea aprobar este reporte consolidado? Puede ingresar
            un comentario u observación adicional de forma opcional.
          </v-alert>

          <v-form
            ref="formAprobar"
            v-model="validAprobar"
            @submit.prevent="confirmarAprobacion"
          >
            <v-textarea
              v-model="comentarioAprobacion"
              label="Comentario u Observación (Opcional)"
              placeholder="Ej: Reporte revisado y aprobado conforme..."
              rows="3"
              outlined
              dense
              auto-grow
              prepend-inner-icon="mdi-comment-check-outline"
              class="rounded-lg"
              hide-details="auto"
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-card-actions
          class="form-card-actions pa-3 pa-sm-4 bg-light border-top d-flex justify-end align-center"
        >
          <v-btn
            outlined
            color="grey darken-1"
            class="btn-form-action text-capitalize px-4 rounded-lg mr-sm-2 font-weight-medium"
            @click="cerrarModalAprobar"
            :disabled="loadingAprobar"
          >
            <v-icon left small>mdi-close</v-icon>
            Cancelar
          </v-btn>

          <v-btn
            color="green darken-1"
            dark
            elevation="2"
            class="btn-form-action text-capitalize px-5 rounded-lg font-weight-bold"
            @click="confirmarAprobacion"
            :loading="loadingAprobar"
          >
            <v-icon left small>mdi-check-bold</v-icon>
            Aprobar Reporte
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { axios2 as axios } from "@/interceptor/useApi";
import router from "@/router";
import { notif } from "@/plugins/notyf";
import Comentario from "../administradorreporte/comentarios.vue";
import global from "@/global";

export default {
  props: [
    "Titulo",
    "Cabecera",
    "loading",
    "Items",
    "Item",
    "ItemTotal",
    "Excel",
    "Crear",
    "Loading",
  ],
  components: {
    Comentario,
  },
  data() {
    return {
      itemSelected: {},
      search: "",
      mostrarIcono: true,
      dialogDelete: false,
      dialog: false,
      dialogPDF: false,
      endpoint: "",
      pdf: null,
      pdfUrl: null,
      loader: false,
      loaderMensaje: "Generando reporte...",
      ver_iframe: false,
      pdfPollingInterval: null,
      dialogRechazar: false,
      validRechazo: false,
      comentarioRechazo: "",
      itemARechazar: null,
      loadingRechazo: false,
      dialogAprobar: false,
      validAprobar: false,
      comentarioAprobacion: "",
      itemAAprobar: null,
      loadingAprobar: false,
      global: global,
    };
  },
  computed: {
    puedeRechazar() {
      return Boolean(
        this.comentarioRechazo && this.comentarioRechazo.trim().length > 0
      );
    },
  },
  methods: {
    ...mapActions("reporteconsolidado", [
      "cargarDatosAdministradorReportes",
      "cargarComentarios",
    ]),

    nuevoItem() {},

    formatearFechaHora(fechaStr) {
      return global.formatearFechaHora(fechaStr);
    },

    descargar() {
      router.push("/reporteconsolidado");
    },

    ver: async function (item) {
      this.ver_iframe = false;
      this.pdfUrl = null;
      this.pdf = null;
      this.loader = true;
      this.loaderMensaje = "Preparando reporte...";
      this.dialogPDF = true;

      if (this.pdfPollingInterval) {
        clearInterval(this.pdfPollingInterval);
        this.pdfPollingInterval = null;
      }

      try {
        const urlParts = item.urlpdf.split("/");
        const uuid = urlParts[urlParts.length - 1];

        this.loaderMensaje = "Generando reporte PDF en segundo plano...";
        await axios.get("reporte/pdf/" + uuid);

        this.pdfPollingInterval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(
              "reporte/pdf/status/" + uuid,
            );
            const statusData = statusResponse.data;

            if (statusData.status === "done" && statusData.url) {
              clearInterval(this.pdfPollingInterval);
              this.pdfPollingInterval = null;
              this.pdfUrl = statusData.url;
              this.pdf = { uuid };
              this.loader = false;
              this.$nextTick(() => {
                this.ver_iframe = true;
              });
            } else if (statusData.status === "failed") {
              clearInterval(this.pdfPollingInterval);
              this.pdfPollingInterval = null;
              this.loader = false;
              this.dialogPDF = false;
              console.error("Error generando PDF:", statusData.error);
              this.$alertify.error(
                statusData.error ||
                  "Ocurrió un error al generar el reporte PDF.",
              );
            } else {
              this.loaderMensaje = "Generando reporte... por favor espere.";
            }
          } catch (pollError) {
            console.error("Error en polling:", pollError);
            clearInterval(this.pdfPollingInterval);
            this.pdfPollingInterval = null;
            this.loader = false;
            this.dialogPDF = false;
            this.$alertify.error(
              "Error de conexión al consultar el estado del reporte.",
            );
          }
        }, 5000);
      } catch (error) {
        console.error("Error al iniciar generación de PDF:", error);
        this.loader = false;
        this.dialogPDF = false;
        this.$alertify.error(
          "No se pudo iniciar la generación del reporte. Intente nuevamente.",
        );
      }
    },

    cerrarVisor() {
      if (this.pdfPollingInterval) {
        clearInterval(this.pdfPollingInterval);
        this.pdfPollingInterval = null;
        if (this.loader && this.pdf && this.pdf.uuid) {
          axios.post("reporte/pdf/cancelar/" + this.pdf.uuid).catch((err) => {
            console.error("Error al cancelar reporte en el backend:", err);
          });
        }
      }
      this.dialogPDF = false;
      this.ver_iframe = false;
      this.pdfUrl = null;
      this.pdf = null;
      this.loader = false;
      this.loaderMensaje = "Generando reporte...";
    },

    comentar: async function (item) {
      await this.cargarComentarios({ id_reportes: item.id });
      this.itemSelected = {
        adm_rep_id: item.id,
        comentario: null,
        estado: item.estado,
      };
      this.dialog = true;
    },

    aprobarItem(item) {
      this.itemAAprobar = item;
      this.comentarioAprobacion = "Reporte Aprobado";
      this.dialogAprobar = true;
      this.$nextTick(() => {
        if (this.$refs.formAprobar) {
          this.$refs.formAprobar.resetValidation();
        }
      });
    },

    cerrarModalAprobar() {
      this.dialogAprobar = false;
      this.itemAAprobar = null;
      this.comentarioAprobacion = "";
      this.loadingAprobar = false;
      if (this.$refs.formAprobar) {
        this.$refs.formAprobar.resetValidation();
      }
    },

    confirmarAprobacion: async function () {
      try {
        this.loadingAprobar = true;
        const url = "administradorreporte/approve";
        const data = {
          id_cliente: localStorage.getItem("idcliente"),
          id_reporte: this.itemAAprobar.id,
          id_usuario: localStorage.getItem("id"),
          comentario: this.comentarioAprobacion
            ? this.comentarioAprobacion.trim()
            : "Reporte Aprobado",
        };

        await axios.post(url, data);
        this.$emit("refrescar");
        notif.success("Reporte aprobado con éxito");
        this.cerrarModalAprobar();
      } catch (error) {
        this.errorMessage = error.response ? error.response.data : error;
        console.error("Error al aprobar reporte:", error);
        this.$alertify.error("Error al procesar la aprobación del reporte.");
        this.loadingAprobar = false;
      }
    },

    rechazarItem(item) {
      this.itemARechazar = item;
      this.comentarioRechazo = "";
      this.dialogRechazar = true;
      this.$nextTick(() => {
        if (this.$refs.formRechazo) {
          this.$refs.formRechazo.resetValidation();
        }
      });
    },

    cerrarModalRechazar() {
      this.dialogRechazar = false;
      this.itemARechazar = null;
      this.comentarioRechazo = "";
      this.loadingRechazo = false;
      if (this.$refs.formRechazo) {
        this.$refs.formRechazo.resetValidation();
      }
    },

    confirmarRechazo: async function () {
      if (!this.$refs.formRechazo || !this.$refs.formRechazo.validate()) {
        return;
      }
      if (!this.comentarioRechazo || !this.comentarioRechazo.trim()) {
        return;
      }

      try {
        this.loadingRechazo = true;
        const url = "administradorreporte/decline";
        const data = {
          id_cliente: localStorage.getItem("idcliente"),
          id_reporte: this.itemARechazar.id,
          id_usuario: localStorage.getItem("id"),
          comentario: this.comentarioRechazo.trim(),
        };

        await axios.post(url, data);
        this.$emit("refrescar");
        notif.success("Reporte rechazado con éxito");
        this.cerrarModalRechazar();
      } catch (error) {
        this.errorMessage = error.response ? error.response.data : error;
        console.error("Error al rechazar reporte:", error);
        this.$alertify.error("Error al procesar el rechazo del reporte.");
        this.loadingRechazo = false;
      }
    },

    DescargarExcel() {
      // Si se requiriese exportación en el futuro
    },
  },
};
</script>

<style scoped>
.min-width-0 {
  min-width: 0;
}

.table-header-toolbar {
  min-height: 58px;
}

.header-main-info {
  overflow: hidden;
}

.header-icon-box {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-company-title {
  font-size: 1.08rem;
  letter-spacing: -0.2px;
}

.header-count-chip {
  font-size: 0.85rem;
  height: 28px;
}

.modern-table ::v-deep table {
  min-width: 880px;
  width: 100%;
}

.modern-table ::v-deep thead th:first-child,
.modern-table ::v-deep tbody tr td:first-child {
  position: sticky !important;
  left: 0 !important;
  background-color: #ffffff !important;
  z-index: 2 !important;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05);
}

.modern-table ::v-deep thead th:first-child {
  background-color: #424242 !important;
  z-index: 3 !important;
}

.modern-table ::v-deep tbody tr:hover td:first-child {
  background-color: #f8fafc !important;
}

.modern-table ::v-deep thead tr:not(.v-data-table__progress) th {
  background-color: #424242 !important;
  color: #ffffff !important;
  font-size: 0.84rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px;
  white-space: nowrap;
  height: 44px !important;
  border-bottom: none !important;
}

.modern-table ::v-deep thead tr:not(.v-data-table__progress) th .v-icon {
  color: #ffffff !important;
}

.modern-table ::v-deep thead tr:not(.v-data-table__progress) th:hover {
  background-color: #333333 !important;
}

.modern-table ::v-deep thead tr.v-data-table__progress th {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  height: auto !important;
}

.modern-table ::v-deep .v-data-table__empty-wrapper td {
  background: transparent !important;
  background-color: transparent !important;
}

.modern-table ::v-deep td {
  font-size: 0.85rem !important;
  padding: 10px 14px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  vertical-align: middle;
}

.modern-table ::v-deep tbody tr:hover {
  background-color: #f8fafc !important;
  transition: background-color 0.15s ease-in-out;
}

.modern-table ::v-deep .v-data-footer {
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
  font-size: 0.82rem;
  padding: 8px 16px;
}

.table-responsive-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-responsive-wrapper::-webkit-scrollbar {
  height: 6px;
}

.table-responsive-wrapper::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.table-responsive-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.table-responsive-wrapper::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.action-btn {
  transition: transform 0.18s ease-in-out, box-shadow 0.18s ease-in-out !important;
}

.action-btn:hover {
  transform: scale(1.1) !important;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2) !important;
}

.gap-1 {
  gap: 6px;
}

.gap-2 {
  gap: 8px;
}

.btn-rechazar {
  transition: all 0.25s ease-in-out !important;
}

.btn-rechazar.v-btn--disabled {
  background-color: #e2e8f0 !important;
  color: #94a3b8 !important;
  border: 1px solid #cbd5e1 !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
  opacity: 1 !important;
}

.btn-rechazar.v-btn--disabled .v-icon {
  color: #94a3b8 !important;
}

.section-title {
  border-left: 3px solid #424242;
  padding-left: 8px;
  margin-bottom: 12px;
}

.bg-light {
  background-color: #f8fafc;
}

.border-top {
  border-top: 1px solid #e2e8f0;
}

.btn-circle-tooltip {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  border-radius: 50% !important;
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  max-width: 36px !important;
  padding: 0 !important;
  margin: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-decoration: none !important;
  transition: all 0.2s ease-in-out !important;
}

.btn-circle-tooltip ::v-deep .v-btn__content {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 100% !important;
  line-height: 1 !important;
}

.btn-circle-tooltip ::v-deep .v-icon {
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.btn-circle-tooltip:hover {
  background: rgba(255, 255, 255, 0.28) !important;
  border-color: rgba(255, 255, 255, 0.45) !important;
}

.pdf-iframe-container {
  width: 100%;
}

.pdf-iframe-view {
  width: 100%;
  height: 75vh;
  border: none;
  display: block;
}

/* ============================================================
   RESPONSIVE: Mobile (max 600px)
   ============================================================ */
@media (max-width: 600px) {
  .table-header-toolbar {
    min-height: 48px;
  }

  .table-header-toolbar ::v-deep .v-toolbar__content {
    padding: 6px 10px !important;
  }

  .header-icon-box {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 6px;
  }

  .header-icon-box .v-icon {
    font-size: 18px !important;
  }

  .header-company-title {
    font-size: 0.88rem !important;
    line-height: 1.2;
  }

  .header-count-chip {
    font-size: 0.75rem !important;
    height: 24px !important;
    padding: 0 6px !important;
  }

  .header-count-chip .v-icon {
    font-size: 14px !important;
    margin-right: 2px !important;
  }

  .search-field {
    width: 100% !important;
    max-width: 100% !important;
    margin-right: 0 !important;
  }

  .action-buttons-group {
    width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
  }

  .btn-action-header {
    width: 100% !important;
    max-width: 100% !important;
    justify-content: center !important;
    margin: 0 !important;
  }

  .modern-table ::v-deep thead th {
    font-size: 0.72rem !important;
    padding: 8px 10px !important;
    height: 38px !important;
  }

  .modern-table ::v-deep td {
    font-size: 0.78rem !important;
    padding: 6px 10px !important;
  }

  .modern-table ::v-deep .v-data-footer {
    flex-wrap: wrap;
    justify-content: center;
    padding: 8px 6px;
    gap: 6px;
  }

  .modern-table ::v-deep .v-data-footer__select {
    margin-right: 6px;
  }

  .modern-table ::v-deep .v-data-footer__pagination {
    margin: 0 4px;
  }

  .pdf-iframe-view {
    height: 55vh;
  }

  .form-card-actions {
    flex-direction: column-reverse !important;
    align-items: stretch !important;
    gap: 8px !important;
    padding: 12px 14px !important;
  }

  .form-card-actions ::v-deep .v-btn,
  .form-card-actions .btn-form-action {
    width: 100% !important;
    max-width: 100% !important;
    display: flex !important;
    justify-content: center !important;
    margin: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .form-card-actions ::v-deep .v-btn + .v-btn {
    margin-left: 0 !important;
  }
}
</style>
